import { Center } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";

import {
  Alert,
  LoadingIndicator,
  SettingsPageContainer,
} from "../../../../components";
import { CoachingForm } from "../../../../shared/coaching";
import { UserOption } from "../../../../shared/coaching/types";
import {
  CurrentUserFragment,
  useOrgUsersByIdsLazyQuery,
} from "../../../graphql";
import useCoachingTask from "./useCoachingTask";
import useCoachingUserOptions, {
  formatUserOption,
} from "./useCoachingUserOptions";
import useUserCanAccessCoaching from "./useUserCanAccessCoaching";

interface CoachingSettingsProps {
  currentUser: CurrentUserFragment;
}

const CoachingSettings: React.FC<CoachingSettingsProps> = ({ currentUser }) => {
  const {
    taskDefinition,
    taskRun,
    updateCoachingTaskDefinition,
    runPreviewCoaching,
  } = useCoachingTask();
  const { userOptions, userOptionsLoading, onInputChange } =
    useCoachingUserOptions();
  const taskDefinitionId = taskDefinition.taskDefinition?.id;

  const [initialSelectedUsers, setInitialSelectedUsers] = useState<
    UserOption[]
  >([]);
  const seededRef = useRef(false);
  const [fetchUsersByIds, { loading: initialUsersLoading }] =
    useOrgUsersByIdsLazyQuery({
      onCompleted: (data) => {
        const fetched = data?.currentUser?.organization?.usersByIds ?? [];
        setInitialSelectedUsers(fetched.map(formatUserOption));
      },
    });

  const config = taskDefinition.parsedConfiguration;
  useEffect(() => {
    if (
      !seededRef.current &&
      config?.filter?.type === "USER" &&
      config.filter.values.length > 0
    ) {
      seededRef.current = true;
      fetchUsersByIds({ variables: { ids: config.filter.values } });
    }
  }, [config, fetchUsersByIds]);

  return (
    <SettingsPageContainer
      maxW={660}
      heading="Coaching preferences"
      subHeading="Customize the content and delivery of interview coaching tips to your interviewers."
    >
      {taskDefinition.loading && (
        <Center>
          <LoadingIndicator />
        </Center>
      )}
      {!taskDefinition.loading && taskDefinition.error && (
        <Alert
          status="error"
          title="Error fetching coaching task"
          description={taskDefinition.error.message}
        />
      )}
      {!taskDefinition.loading &&
        !taskDefinition.error &&
        taskDefinition.taskDefinition &&
        taskDefinitionId &&
        taskDefinition.parsedConfiguration && (
          <CoachingForm
            configuration={taskDefinition.parsedConfiguration}
            taskRunResult={taskRun}
            userOptions={userOptions}
            userOptionsLoading={userOptionsLoading}
            onUserSearchInputChange={onInputChange}
            initialSelectedUsers={initialSelectedUsers}
            initialUsersLoading={initialUsersLoading}
            onPreview={(previewUserId, config) =>
              runPreviewCoaching.mutation({
                interviewerId: previewUserId,
                taskDefinitionId,
                config,
              })
            }
            onSave={(config) => {
              updateCoachingTaskDefinition.mutation({
                id: taskDefinitionId,
                config,
              });
            }}
            saveLoading={updateCoachingTaskDefinition.loading}
          />
        )}
    </SettingsPageContainer>
  );
};

// TODO: Move this wrapper and coaching access logic to access map
const CoachingSettingsWrapper: React.FC<CoachingSettingsProps> = (
  props: CoachingSettingsProps
) => {
  const { currentUser } = props;
  const userCanAccessCoaching = useUserCanAccessCoaching(currentUser);

  if (!userCanAccessCoaching) {
    return <Navigate to="/not-found" replace />;
  }

  return <CoachingSettings {...props} />;
};

export default CoachingSettingsWrapper;
